import { useLingui } from '@lingui/react/macro'
import { useRouter } from '@tanstack/react-router'
import type { CommandPalettePageTarget } from 'features/commandPalette/utils/commandPaletteTargets'
import { NAV_ITEMS_CONFIG, type NavItemConfig } from 'features/navbar/navConfig'
import type { SelectedTab } from 'features/navbar/types'
import { useMemo } from 'react'

import { useIsNavItemPermitted } from './useIsNavItemPermitted'

export const PAGE_KIND_LABEL = 'Pages'

function isNavItemEntry(
  entry: [string, NavItemConfig]
): entry is [SelectedTab, NavItemConfig] {
  return Object.prototype.hasOwnProperty.call(NAV_ITEMS_CONFIG, entry[0])
}

/** The sidebar pages this user can open for the site, with the sidebar's own gates. */
export function useCommandPalettePageTargets({
  siteId,
  orgId,
}: {
  siteId: string | undefined
  orgId: string | undefined
}): CommandPalettePageTarget[] {
  const { i18n } = useLingui()
  const router = useRouter()
  const isPermitted = useIsNavItemPermitted({ siteId, orgId })

  return useMemo(
    () =>
      Object.entries(NAV_ITEMS_CONFIG)
        .filter(isNavItemEntry)
        .filter(([tabKey]) => isPermitted(tabKey))
        .map(([tabKey, config]) => ({
          id: `page:${tabKey}`,
          kind: 'page' as const,
          label: i18n._(config.label),
          kindLabel: PAGE_KIND_LABEL,
          icon: config.icon,
          destination: tabKey,
          href: router.buildLocation({
            to: config.to,
            params: config.params?.(siteId ?? '', orgId),
            search: config.search?.(siteId ?? '', orgId),
          }).href,
        })),
    [i18n, isPermitted, orgId, router, siteId]
  )
}
